import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  IAdminAlert,
  IAdminAlertHistoryEntry,
} from "../../../../@types/admin/AdminAlert";
import { TableSorted } from "../../../../components/TableSorted";
import { useAdminAlertHistory } from "../../../../hooks/queries/admin/alerts";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import {
  ErrorMessage,
  ModalContainer,
  ModalHeader,
  ModalSubtitle,
  ModalTitle,
  StatusBadge,
} from "../AdminPage.styles";
import { TableContainer } from "../TableContainer";
import { TableLoadingState } from "../TableStates";
import { TableWrapper } from "../TableWrapper";
import { alertChangeBadgeStatus } from "./alertChangeType";

interface IAlertHistoryModalProps {
  alert: IAdminAlert;
}

/**
 * One alert's change history — Retool's `modalAlertsHistory` (EN-5165).
 *
 * Retool opened this on a row double-click, which is undiscoverable; it opens
 * from a row action here. Its Object column concatenated the patient's email
 * and the trigger summary into one string; those arrive as separate fields
 * and are shown as separate columns, so both are sortable and searchable.
 */
export function AlertHistoryModal({ alert }: IAlertHistoryModalProps) {
  const { history, isLoading, isError } = useAdminAlertHistory(alert.id);

  const columns: ColumnDef<IAdminAlertHistoryEntry, string>[] = useMemo(
    () => [
      {
        accessorKey: "changedAt",
        header: "Date / time",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.changedAt, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        // Through an accessorFn so the badge still renders for a falsy-looking
        // value — TableSorted skips a cell renderer whose value is falsy.
        id: "changeType",
        header: "Change",
        accessorFn: (entry) => entry.changeType,
        cell: ({ row }) => (
          <StatusBadge status={alertChangeBadgeStatus(row.original.changeType)}>
            {row.original.changeType}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      { accessorKey: "alertName", header: "Alert", enableSorting: true },
      {
        accessorKey: "summaryText",
        header: "Trigger summary",
        enableSorting: true,
      },
      {
        accessorKey: "changedByEmail",
        header: "Changed by",
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>Alert change history</ModalTitle>
        <ModalSubtitle>
          {alert.name ?? `Alert ${alert.id}`}
          {alert.patientEmail ? ` · ${alert.patientEmail}` : ""}
        </ModalSubtitle>
      </ModalHeader>

      {isError && (
        <ErrorMessage>
          Failed to load this alert&apos;s history. Close the dialog and try
          again.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-alert-history"]]}>
          <TableSorted
            columns={columns}
            data={history}
            emptyStateText={
              isLoading ? (
                <TableLoadingState />
              ) : (
                "No changes have been recorded"
              )
            }
          />
        </TableWrapper>
      </TableContainer>
    </ModalContainer>
  );
}
